import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 450px;
  width: 100%;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  text-align: center;
`;

const ModalIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 1.5rem auto;
  border-radius: 50%;
  background-color: #fed7d7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e53e3e;
  font-size: 24px;
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: bold;
`;

const ModalMessage = styled.p`
  margin: 0;
  color: #718096;
  line-height: 1.6;
`;

const ModalBody = styled.div`
  padding: 0 2rem 2rem 2rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  &.confirm {
    background-color: #e53e3e;
    color: white;

    &:hover {
      background-color: #c53030;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(229, 62, 62, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.cancel {
    background-color: #f7fafc;
    color: #4a5568;
    border: 2px solid #e2e8f0;

    &:hover {
      background-color: #edf2f7;
      border-color: #cbd5e0;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger" // danger, warning, info
}) => {
  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '⚠️';
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <ModalContent
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2, type: "spring", damping: 25, stiffness: 300 }}
          >
            <ModalHeader>
              <ModalIcon>
                {getIcon()}
              </ModalIcon>
              <ModalTitle>{title}</ModalTitle>
              <ModalMessage>{message}</ModalMessage>
            </ModalHeader>

            <ModalBody>
              <ModalActions>
                <ModalButton
                  className="cancel"
                  onClick={onClose}
                  autoFocus
                >
                  {cancelText}
                </ModalButton>
                <ModalButton
                  className="confirm"
                  onClick={handleConfirm}
                >
                  {confirmText}
                </ModalButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;